import { splitDiscord, stamp } from '@itc/shared';
import { KB_CATEGORIES, MECHANICS, NOTE_STATUS, REPORT_TYPES } from './constants.ts';
import type { Entry, EntryStatus, KbCategory } from './schemas.ts';

// The Library's rules (plan 2E R1, R2 and R5), after the panel's vKB, noteSheet, kbPost,
// vReportsBody, updCount and genTemplate.

export const statusLabel = (status: EntryStatus): string => NOTE_STATUS.find((entry) => entry.value === status)?.label ?? '';

/** R1: status and patch exist only for Mechanics & bugs. */
export function entryFields<T extends { category: KbCategory; status: EntryStatus; patch: string }>(fields: T): T {
  return fields.category === MECHANICS ? fields : { ...fields, status: '', patch: '' };
}

type Listed = Pick<Entry, 'category' | 'updated'>;

/** The categories that hold entries, in R1's order, each newest change first. */
export function groupEntries<T extends Listed>(entries: readonly T[]): { category: KbCategory; entries: T[] }[] {
  return KB_CATEGORIES.map((category) => ({ category, entries: entries.filter((entry) => entry.category === category).sort((a, b) => b.updated.localeCompare(a.updated)) })).filter((group) => group.entries.length > 0);
}

/** R1's search: case-insensitive, on title, body, patch and category. A blank query matches everything. */
export function entryMatches(entry: Pick<Entry, 'title' | 'body' | 'patch' | 'category'>, query: string): boolean {
  const wanted = query.trim().toLowerCase();
  return wanted === '' || [entry.title, entry.body, entry.patch, entry.category].join(' ').toLowerCase().includes(wanted);
}

/** Copy for Discord (kbPost). */
export function entryPost(entry: Pick<Entry, 'title' | 'category' | 'status' | 'patch' | 'body'>): string {
  const mechanics = entry.category === MECHANICS;
  const meta = [entry.category, ...(mechanics && entry.status !== '' ? [statusLabel(entry.status)] : []), ...(mechanics && entry.patch !== '' ? [entry.patch] : [])].join(' · ');
  return [`## ${entry.title}`, `*${meta}*`, ...(entry.body === '' ? [] : ['', entry.body])].join('\n');
}

/** Entries waiting on a PTU check, for the Verify on PTU widget (R18). */
export const waitingOnPtu = <T extends Pick<Entry, 'status'>>(entries: readonly T[]): T[] => entries.filter((entry) => entry.status === 'verify');

/** R2: a type's label. An unknown type shows as "Report". */
export const reportLabel = (type: string): string => REPORT_TYPES.find((entry) => entry.type === type)?.label ?? 'Report';

/** What a saved report's group is called: its label, or Other for an unknown type. */
export const OTHER_GROUP = 'Other';

/** R2: groups in REPORT_TYPES' order, then Other, each newest change first. */
export function groupReports<T extends { type: string; updated: string }>(reports: readonly T[]): { title: string; reports: T[] }[] {
  const known = new Set<string>(REPORT_TYPES.map((entry) => entry.type));
  const sorted = [...reports].sort((a, b) => b.updated.localeCompare(a.updated));
  return [...REPORT_TYPES.map((entry) => ({ title: entry.label, reports: sorted.filter((report) => report.type === entry.type) })), { title: OTHER_GROUP, reports: sorted.filter((report) => !known.has(report.type)) }].filter((group) => group.reports.length > 0);
}

/** R2: a list's preview: 300 characters, with Discord's timestamps as `[time]`. */
export const reportPreview = (body: string): string => body.replace(/<t:-?\d+:\w>/g, '[time]').slice(0, 300);

/** R2: the editor's count (`updCount`): `1,240 / 2,000`, and past that how it posts. */
export function countText(body: string): string {
  const count = `${body.length.toLocaleString('en-US')} / 2,000`;
  return body.length > 2000 ? `${count} · posts as ${String(splitDiscord(body).length)} messages` : count;
}

const TEMPLATES = {
  intel: ['## Intel report', 'Location', 'Observed', 'Ships / numbers', 'Threat level', 'Recommended action'],
  incident: ['## Incident report', 'Location', 'Involved', 'What happened', 'Losses', 'Follow-up'],
} as const;

/** R5: `genTemplate`, with the time filled in and the other headings blank. */
export function genTemplate(type: keyof typeof TEMPLATES, now: Date): string {
  const [title, ...headings] = TEMPLATES[type];
  return [title, `**Time:** ${stamp(now, 'f')}`, ...headings.map((heading) => `**${heading}:** `)].join('\n');
}

/** The local day a `YYYY-MM-DD` date names: a date alone would be read as UTC. */
const localDay = (date: string) => {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** R17: calendar days to a date, never below 0. By day number, so a clock change can't add one. */
export function daysUntil(date: string, now: Date): number {
  const target = localDay(date);
  const days = (Date.UTC(target.getFullYear(), target.getMonth(), target.getDate()) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / (24 * 60 * 60 * 1000);
  return Math.max(0, days);
}

/** `October 30, 2026`. */
export const dateLabel = (date: string) => localDay(date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
